import React, { useMemo, useRef, useState } from 'react';
import useOrganization from '../../../hooks/use-organization';
import { PaginationTable } from '../../../components/Redesing/table/pagination-table';
import { organizationAdminsColumns } from '../../../tableColumns/organizationAdminsTable';
import CardView from '../../../components/Redesing/card-view';
import {
  IColumn,
  ISortConfig,
} from '../../../ts/interface/components/redesing/table.interface';
import IconButtonMenu from '../../../components/Redesing/icon-button-menu';
import AddIcon from '@mui/icons-material/Add';
import { Stack } from '@mui/material';
import MovaticCustomModal from '../../../components/Modal/MovaticCustomModal';
import { history } from '../../../utils';
import { removeAdminFromOrganizationMutation } from '../../../hooks/admins/mutation/V2';
import CustomToast from '../../../components/Redesing/custom-toast';
import { IOrganizationAdmin } from '../../../ts/interface/organization.interface';

import { DEFAULT_ROWS_PER_PAGE } from '../../../constants';
import useSearchDebounce from '../../../hooks/search/useSearchDebounce';
import {
  useOrganizationAdminsRelayQuery,
  mapRelayOrgAdminsToIOrganizationAdmins,
  OrganizationAdminSortInput,
} from '../../../hooks/organization/query/V2';
import CustomAsyncToast from '../../../components/Redesing/custom-async-toast';

const sortFieldMap: Record<string, OrganizationAdminSortInput['field']> = {
  joined_at: 'CREATED_ON',
};

function OrganizationAdmins() {
  const organizationInfo = useOrganization();

  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_ROWS_PER_PAGE);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<ISortConfig>({
    key: 'joined_at',
    direction: 'desc',
  });
  const [exportData, setExportData] = useState(false);

  const [afterCursor, setAfterCursor] = useState<string | null>(null);
  const [accumulatedAdmins, setAccumulatedAdmins] = useState<IOrganizationAdmin[]>([]);

  const debouncedSearchTerm = useSearchDebounce(search);

  const adminsFilter = useMemo(() => {
    if (!organizationInfo?.id) return undefined;
    return {
      organizationId: organizationInfo.id,
      ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
    };
  }, [organizationInfo?.id, debouncedSearchTerm]);

  const graphqlSort: OrganizationAdminSortInput[] = useMemo(() => {
    const field = sortFieldMap[sort.key] ?? 'CREATED_ON';
    return [{ field, order: sort.direction === 'asc' ? 'ASC' : 'DESC' }];
  }, [sort]);

  const {
    data: graphqlData,
    count: totalAdmins,
    hasMore: graphqlHasMore,
    endCursor,
    isLoading,
    isRefetching,
    refetch,
  } = useOrganizationAdminsRelayQuery(adminsFilter, {
    limit: 100,
    after: afterCursor,
    sort: graphqlSort,
    enabled: Boolean(organizationInfo?.id),
  });

  const adminRows = useMemo(
    () => (graphqlData ? mapRelayOrgAdminsToIOrganizationAdmins(graphqlData) : []),
    [graphqlData]
  );

  useMemo(() => {
    if (!adminRows.length && !afterCursor) {
      setAccumulatedAdmins([]);
      return;
    }
    if (adminRows.length) {
      if (afterCursor) {
        setAccumulatedAdmins((prev) => [...prev, ...adminRows]);
      } else {
        setAccumulatedAdmins(adminRows);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminRows]);

  const admins = accumulatedAdmins.length > 0 ? accumulatedAdmins : adminRows;

  const selectedAdmin = useRef<IOrganizationAdmin | null>(null);
  const [removeAdminConfirm, setRemoveAdminConfirm] = useState(false);

  const resetPagination = () => {
    setAccumulatedAdmins([]);
    setAfterCursor(null);
    setPageNumber(0);
  };

  const resetStates = () => {
    resetPagination();
    setPageSize(DEFAULT_ROWS_PER_PAGE);
    setSearch('');
    setSort({
      key: 'joined_at',
      direction: 'desc',
    });
    selectedAdmin.current = null;
    setRemoveAdminConfirm(false);
  };

  // Remove the admin from the organization via GraphQL, revoking every role and
  // permission they hold there. Replaces the legacy REST DELETE flow
  // (`addUpdateAdminOrganization(..., 'DELETE', adminId)`). The mutation resolves
  // with a result envelope rather than rejecting, so we throw on failure to drive
  // the toast's error state and skip the refetch.
  const removeAdmin = async () => {
    const adminEmail = selectedAdmin.current?.admin_email;
    if (!adminEmail || !organizationInfo?.id) {
      CustomToast({
        message: 'Failed to remove admin',
        type: 'error',
      });
      return;
    }
    CustomAsyncToast({
      promise: () =>
        removeAdminFromOrganizationMutation({
          email: adminEmail,
          organizationId: organizationInfo.id,
        }).then((res) => {
          if (!res.isSuccess) {
            throw new Error(res.errors?.[0]?.message || 'Failed to remove admin');
          }
          return res;
        }),
      successMessage: () => 'Admin removed successfully',
      loadingMessage: () => `Removing admin...`,
      errorMessage: () => `Error removing admin`,
    }).then((response) => {
      if (response) {
        resetStates();
        refetch();
      }
    });
  };

  const confirmationModal = () => {
    return (
      <>
        <MovaticCustomModal
          title={'Remove Admin'}
          open={removeAdminConfirm}
          onClose={() => setRemoveAdminConfirm(!removeAdminConfirm)}
          content={
            <div>
              Are you sure you would like to remove{' '}
              <b>{selectedAdmin.current?.admin_name}</b>
              from the organization <b>{organizationInfo.name}</b>?
            </div>
          }
          action={
            <IconButtonMenu
              buttons={[
                {
                  label: 'Cancel',
                  onClick: () => setRemoveAdminConfirm(!removeAdminConfirm),
                },
                {
                  label: 'Remove',
                  onClick: () => removeAdmin(),
                },
              ]}
            />
          }
        />
      </>
    );
  };

  const handleSelectedAdmin = (admin: IOrganizationAdmin) => {
    selectedAdmin.current = admin;
  };

  return (
    <CardView
      title={'Admins'}
      headerActions={
        <IconButtonMenu
          buttons={[
            {
              label: 'Add',
              startIcon: <AddIcon />,
              onClick: () =>
                history.push({
                  pathname: '/settings/admin/add',
                  search: `?organizationId=${organizationInfo?.id}`,
                }),
            },
          ]}
        />
      }
      content={
        <Stack>
          <PaginationTable
            showFilter={false}
            isLoading={
              isLoading || (pageNumber > 0 && admins.length <= pageNumber * pageSize)
            }
            isLoadingCache={isRefetching}
            padding={{ pt: 1, pl: 0, pr: 1 }}
            exportData={exportData}
            csvFileName={'Organization_Admins.csv'}
            onExport={() => setExportData(false)}
            hasMore={graphqlHasMore || admins.length > (pageNumber + 1) * pageSize}
            localSearch={false}
            customKey={'admin_email'}
            onRowCLick={(adminEmail: string) =>
              history.push({
                pathname: `/settings/admin/${adminEmail}`,
                search: `?organizationId=${organizationInfo?.id}`,
              })
            }
            columns={
              organizationAdminsColumns(
                handleSelectedAdmin,
                setRemoveAdminConfirm,
                (admin: IOrganizationAdmin) =>
                  history.push({
                    pathname: `/settings/admin/${admin.admin_email}`,
                    search: `?organizationId=${organizationInfo?.id}&edit=1`,
                  })
              ) as IColumn[]
            }
            items={admins}
            totalItems={totalAdmins}
            page={pageNumber}
            rowsPerPage={pageSize}
            onPageChange={(_: any, newPage: number) => {
              const incrementedPage = newPage + 1;
              if (admins?.length && admins.length / pageSize < incrementedPage) {
                setAfterCursor(endCursor || null);
              }
              setPageNumber(newPage);
            }}
            onRowsPerPageChange={(pageSizeString) => {
              resetPagination();
              setPageSize(Number(pageSizeString.target.value));
            }}
            handleSearch={(e: React.ChangeEvent<HTMLInputElement>) => {
              resetPagination();
              setSearch(e.target.value);
            }}
            sortBy={sort.key}
            sortDirection={sort.direction as 'desc' | 'asc'}
            onSortChange={(newSort: any) => {
              resetPagination();
              setSort(newSort);
            }}
            searchText={search}
          />
          {confirmationModal()}
        </Stack>
      }
    />
  );
}

export default OrganizationAdmins;
